'use client'

import { type ReactNode, useRef } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Backdrop } from './Backdrop'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  title?: string
  ariaLabel?: string
  showCloseButton?: boolean
  closeOnBackdropClick?: boolean
  maxWidth?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | 'full'
  fullScreen?: boolean
  className?: string
  backdropOpacity?: 'light' | 'medium' | 'dark' | 'soft' | 'subtle'
  centered?: boolean
  zIndex?: number
}

const maxWidthClasses = {
  xs: 'max-w-xs',
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  full: 'max-w-full',
}

export const Modal = ({
  isOpen,
  onClose,
  children,
  title,
  ariaLabel,
  showCloseButton = true,
  closeOnBackdropClick = true,
  maxWidth = 'md',
  fullScreen = false,
  className = '',
  backdropOpacity = 'medium',
  centered = true,
  zIndex = 50,
}: ModalProps) => {
  const handleBackdropClick = closeOnBackdropClick ? onClose : undefined
  const maxWidthClass = maxWidthClasses[maxWidth]
  const panelRef = useRef<HTMLDivElement>(null)

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      initialFocus={panelRef}
      aria-label={ariaLabel}
      className="relative"
      style={{ zIndex }}
    >
      <Backdrop
        isOpen={isOpen}
        onClose={handleBackdropClick}
        opacity={backdropOpacity}
      />

      <div
        className={`fixed inset-0 w-screen ${fullScreen ? '' : 'overflow-y-auto'}`}
        style={{ zIndex: zIndex + 1 }}
      >
        <div
          className={`
                    flex min-h-full ${centered ? 'justify-center items-center' : 'justify-center'} 
                    p-4 text-center ${centered ? '' : 'items-start pt-16'} sm:p-0
                `}
        >
          <DialogPanel
            ref={panelRef}
            tabIndex={-1}
            transition
            className={`
                            relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl 
                            transition-all data-closed:translate-y-4 data-closed:opacity-0 
                            data-enter:duration-300 data-leave:duration-200 
                            data-enter:ease-out data-leave:ease-in 
                            ${fullScreen ? 'w-full h-full' : `w-full ${maxWidthClass}`}
                            ${fullScreen ? 'rounded-none' : ''}
                            sm:data-closed:translate-y-0 sm:data-closed:scale-95
                            ${className}
                        `.trim()}
          >
            {title && (
              <div className="px-4 py-3 border-b border-gray-200 relative">
                <h3 className="text-lg font-medium text-gray-900 pr-8">
                  {title}
                </h3>
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="absolute top-1/2 right-4 -translate-y-1/2 z-10 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                    aria-label="Close modal"
                  >
                    <XMarkIcon className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                  </button>
                )}
              </div>
            )}

            {!title && showCloseButton && (
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                aria-label="Close modal"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 hover:text-gray-700" />
              </button>
            )}

            <div className={title ? 'p-4' : 'p-0'}>{children}</div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}
